import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { eps } from '../../../../services/url.service'
import { Store } from '@ngrx/store'
import {
  selectArrearLopDays,
  selectIsTdsPreviewLoading,
  selectPreviousSalaries,
  selectRunningPayroll,
  selectTdsCalculation,
  selectTdsCalculationBatch
} from '../../../../store/payroll/payroll.reducers'
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  skip,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs'
import { selectBusinesses } from '../../../../store/business/business.reducer'
import { CommonModule } from '@angular/common'
import { loadBusinesses } from '../../../../store/business/business.actions'
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'
import {
  IncomingPayroll,
  IncomingRunningPayrollBatch,
  Payroll,
  PayrollCalculationBreakdown,
  PayrollCalculationContext,
  PayrollStatus,
  RunningPayrollBatch,
  TdsCalculationBatchInput,
  TdsCalculationInput
} from '../../../../store/payroll/payroll.state'
import { EmployeeSalary } from '../../../../store/employee-salary/employee-salary.state'
import {
  runningPayrollActions,
  tdsCalculationActions
} from '../../../../store/payroll/payroll.actions'
import { environment } from '../../../../../environments/environment'
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { KeySettingsComponent } from '../key-settings/key-settings.component'
import { PayslipComponent } from '../payslip/payslip.component'
import { ClockInClockOutService } from '../../../../services/clockIn-clockOut.service'
import { ToastrService } from 'ngx-toastr'
import {
  getDaysInPeriod,
  getInitials,
  getUpdatedEffectiveFromDate
} from '../../../../utils/payroll.utils'
import { PayrollCalculationService } from '../../../../services/payroll-calculation.service'
import { filterRelevantPayrolls, prepareDataLoadRequests } from '../payroll-data.helpers'
import { selectUser } from '../../../../store/auth/auth.state'
import * as Papa from 'papaparse'

interface PayrollOverrideRow {
  empId: string
  grossSalary?: string
  netSalary?: string
  tdsAfterCess?: string
  lopDays?: string
  bonus?: string
  expenses?: string
  adjustments?: string
  rounding?: string
  esi?: string
  esiEmployer?: string
}

interface EmployeeResignation {
  status: string
  isFullNFinal: boolean
  effectiveLastDay?: string | Date | null
  date?: string | Date | null
}

@Component({
  selector: 'app-earnings',
  imports: [RouterModule, CommonModule, ReactiveFormsModule, CapitalizePipe],
  templateUrl: './earnings.component.html',
  styleUrl: './earnings.component.scss'
})
export class EarningsComponent implements OnInit, OnDestroy {
  private store = inject(Store)
  private router = inject(Router)
  private route = inject(ActivatedRoute)
  private toastr = inject(ToastrService)
  private fb = inject(FormBuilder)
  private modalService = inject(NgbModal)
  private ClockInClockOutService = inject(ClockInClockOutService)
  private payrollCalcService = inject(PayrollCalculationService)
  private destroy$ = new Subject<void>()
  @Output() nextScreen = new EventEmitter<boolean>()
  KeySettingsComponent = KeySettingsComponent
  PayslipComponent = PayslipComponent
  imageBaseUrl = environment.apiBaseUrl + '/uploads/'
  eps = eps

  //Utils and helpers
  getInitials = getInitials
  getUpdatedEffectiveFromDate = getUpdatedEffectiveFromDate
  prepareDataLoadRequests = prepareDataLoadRequests
  filterRelevantPayrolls = filterRelevantPayrolls

  payrollBatchForm!: FormGroup
  runningPayroll$ = this.store.select(selectRunningPayroll)
  currentIncomingPayrolls: IncomingPayroll[] = []
  previousSalaries$ = this.store.select(selectPreviousSalaries)
  arrearLopDays$ = this.store.select(selectArrearLopDays)
  tdsPreviewResults$ = this.store.select(selectTdsCalculationBatch)
  isTdsPreviewLoading$ = this.store.select(selectIsTdsPreviewLoading)
  payPeriodStartDate = ''
  payPeriodEndDate = ''
  businessEntity$!: Observable<string | undefined>
  financialYear = ''

  changeScreen(value: boolean) {
    this.nextScreen.emit(value)
  }

  get payrollsFormArray(): FormArray {
    return this.payrollBatchForm.get('payrolls') as FormArray
  }

  ngOnInit(): void {
    this.initializeForm()
    this.store.dispatch(loadBusinesses({}))
    this.triggerInitialDataLoad()
    this.setFinancialYear()
    this.initializeBusinessEntityObservable()
    this.subscribeToDataAndBuildForm()
    this.handleSingleRowUpdates()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private initializeForm(): void {
    this.payrollBatchForm = this.fb.group({
      payrolls: this.fb.array([])
    })
  }

  private triggerInitialDataLoad(): void {
    this.runningPayroll$
      .pipe(
        filter(
          (rp): rp is IncomingRunningPayrollBatch =>
            !!rp && !!rp.payPeriodStart && !!rp.payPeriodEnd && !!rp.belongsTo
        ),
        take(1),
        tap(rp => {
          this.store.dispatch(
            runningPayrollActions.loadRunningPayroll({
              payPeriodStart: rp.payPeriodStart,
              payPeriodEnd: rp.payPeriodEnd,
              belongsTo: rp.belongsTo
            })
          )
        })
      )
      .subscribe()
  }

  private subscribeToDataAndBuildForm(): void {
    // STREAM 1: Trigger the calculation
    combineLatest([
      this.runningPayroll$.pipe(
        filter(
          (rp): rp is IncomingRunningPayrollBatch =>
            !!rp && !!rp.payrolls && !!rp.payPeriodStart && !!rp.payPeriodEnd
        )
      ),
      this.previousSalaries$,
      this.arrearLopDays$
    ])
      .pipe(
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        map(([runningPayrollBatch, prevSalariesMap, arrearLopsMap]) => {
          this.handleDataFetchingNeeds(runningPayrollBatch, prevSalariesMap, arrearLopsMap)

          const payrollsWithGross = this.currentIncomingPayrolls.map(ip => {
            const previousSalary = prevSalariesMap?.[ip.employeeId] ?? null
            const lopDaysForArrears = arrearLopsMap?.[ip.employeeId] ?? 0
            const payPeriodStart = new Date(this.payPeriodStartDate)
            const payPeriodEnd = new Date(this.payPeriodEndDate)
            // const activeResignation = ip.employee.employeeResignations?.find(
            //   r => r.status === 'Approved' && r.isFullNFinal === false && r.isSalaryHold === true
            // )
            // const activeResignation = ip.employee.employeeResignations?.find(r => {
            //   if (
            //     r.status !== 'Approved' ||
            //     r.isFullNFinal === true ||
            //     !r.isSalaryHold ||
            //     !r.salaryHoldFrom
            //   ) {
            //     return false
            //   }

            //   const holdFrom = new Date(r.salaryHoldFrom)
            //   const holdTo = r.salaryHoldTo ? new Date(r.salaryHoldTo) : new Date('9999-12-31')

            //   // ✅ Check overlap with pay period
            //   return holdFrom <= payPeriodEnd && holdTo >= payPeriodStart
            // })

            const activeResignation = ip.employee.employeeResignations?.find(r => {
              if (r.status !== 'Approved' || r.isFullNFinal === true) {
                return false
              }

              const lastWorkingDay = r.effectiveLastDay ? new Date(r.effectiveLastDay) : null

              const holdFrom =
                r.isSalaryHold && r.salaryHoldFrom ? new Date(r.salaryHoldFrom) : null

              // No useful date → not relevant
              if (!lastWorkingDay && !holdFrom) {
                return false
              }

              // Salary hold case
              if (r.isSalaryHold && holdFrom) {
                return holdFrom <= payPeriodEnd && holdFrom >= payPeriodStart
              }

              // No salary hold case
              if (lastWorkingDay) {
                return lastWorkingDay >= payPeriodStart && lastWorkingDay <= payPeriodEnd
              }

              return false
            })
            const context: PayrollCalculationContext = {
              currentSalary: ip.salary,
              previousSalary: previousSalary,
              payPeriodStart: this.payPeriodStartDate,
              payPeriodEnd: this.payPeriodEndDate,
              lopDays: ip.lopDays ?? 0,
              lopDaysForArrearsPeriod: lopDaysForArrears,
              bonus: ip.bonus ?? 0,
              expenses: ip.expenses ?? 0,
              adjustments: ip.adjustments ?? 0,
              rounding: ip.rounding ?? 0,
              tdsAfterCess: Math.ceil(ip.tdsAfterCess ?? 0),
              salaryHoldFrom: activeResignation?.salaryHoldFrom ?? null,
              salaryHoldTo: activeResignation?.salaryHoldTo ?? null,
              esi: Math.ceil(ip.esi ?? 0),
              esiEmployer: Math.ceil(ip.esiEmployer ?? 0)
            }

            // Before calling calculation, determine active resignation and LOP
            // const activeResignation = ip.employee.employeeResignations?.find(
            //   r => r.status === 'Approved' && r.isFullNFinal === false && r.isSalaryHold === true
            // )
            // const localResults = this.payrollCalcService.calculateGrossMetrics(
            //   context,
            //   ip.employee.joiningDate,
            //   activeResignation?.salaryHoldFrom,
            //   activeResignation?.salaryHoldTo,
            //   activeResignation ? true : false
            // )

            //for offboarding
            const employee = (
              context.currentSalary as EmployeeSalary & {
                employee?: {
                  employeeResignations?: EmployeeResignation[]
                }
              }
            ).employee

            const resignations: EmployeeResignation[] = employee?.employeeResignations ?? []

            const validResignation =
              resignations
                .filter(
                  (resignation: EmployeeResignation) =>
                    resignation.status === 'Approved' && !resignation.isFullNFinal
                )
                .sort((a: EmployeeResignation, b: EmployeeResignation) => {
                  const aDate = a.effectiveLastDay ? new Date(a.effectiveLastDay).getTime() : 0

                  const bDate = b.effectiveLastDay ? new Date(b.effectiveLastDay).getTime() : 0

                  return bDate - aDate
                })[0] ?? null

            const effectiveLastDay = validResignation?.effectiveLastDay
              ? new Date(validResignation.effectiveLastDay)
              : null

            // console.log('employee effective last date:', effectiveLastDay)
            const localResults = this.payrollCalcService.calculateGrossMetrics(
              context,
              ip.employee.joiningDate,
              // activeResignation?.effectiveLastDay ?? null,
              effectiveLastDay,
              activeResignation?.salaryHoldFrom ?? null,
              activeResignation?.salaryHoldTo ?? null,
              activeResignation?.isSalaryHold === true
            )
            // console.log('local results', localResults)
            return {
              employeeId: ip.employeeId,
              currentMonthGross: localResults.grossSalary,
              lopDays: ip.lopDays ?? 0,
              bonus: ip.bonus ?? 0,
              expenses: ip.expenses ?? 0,
              adjustments: ip.adjustments ?? 0,
              rounding: ip.rounding ?? 0,
              tdsAfterCess: Math.ceil(ip.tdsAfterCess ?? 0),
              esi: Math.ceil(ip.esi ?? 0),
              esiEmployer: Math.ceil(ip.esiEmployer ?? 0)
            }
          })
          if (payrollsWithGross.length === 0) return null

          return {
            payPeriodEnd: this.payPeriodEndDate,
            employees: payrollsWithGross
          } as TdsCalculationBatchInput
        }),
        filter((payload): payload is TdsCalculationBatchInput => !!payload), // Ensure payload is not null
        takeUntil(this.destroy$)
      )
      .subscribe(payload => {
        this.store.dispatch(tdsCalculationActions.loadBatchTdsPreview({ payload }))
      })
    // STREAM 2: Listen for ALL data to be ready, then build the form
    combineLatest([
      this.runningPayroll$.pipe(filter(rp => !!rp)), // The base data
      this.previousSalaries$,
      this.arrearLopDays$,
      this.tdsPreviewResults$.pipe(filter(res => res !== null)) // Wait for TDS results
    ])
      .pipe(
        // This stream now has everything needed to build the form
        // eslint-disable-next-line @typescript-eslint/naming-convention
        map(([_, prevSalariesMap, arrearLopsMap, tdsMap]) => {
          // We need to recalculate gross here one last time to have it available for building context
          return this.currentIncomingPayrolls.map(ip => {
            const previousSalary = prevSalariesMap?.[ip.employeeId] ?? null
            const lopDaysForArrears = arrearLopsMap?.[ip.employeeId] ?? 0
            const payPeriodStart = new Date(this.payPeriodStartDate)
            const payPeriodEnd = new Date(this.payPeriodEndDate)
            const activeResignation = ip.employee.employeeResignations?.find(r => {
              if (
                r.status !== 'Approved' ||
                r.isFullNFinal === true ||
                !r.isSalaryHold ||
                !r.salaryHoldFrom
              ) {
                return false
              }

              const holdFrom = new Date(r.salaryHoldFrom)
              const holdTo = r.salaryHoldTo ? new Date(r.salaryHoldTo) : new Date('9999-12-31')

              // ✅ Check overlap with pay period
              return holdFrom <= payPeriodEnd && holdTo >= payPeriodStart
            })
            const context: PayrollCalculationContext = {
              currentSalary: ip.salary,
              previousSalary: previousSalary,
              payPeriodStart: this.payPeriodStartDate,
              payPeriodEnd: this.payPeriodEndDate,
              lopDays: ip.lopDays ?? 0,
              lopDaysForArrearsPeriod: lopDaysForArrears,
              bonus: ip.bonus ?? 0,
              expenses: ip.expenses ?? 0,
              adjustments: ip.adjustments ?? 0,
              rounding: ip.rounding ?? 0,
              tdsAfterCess: Math.ceil(ip.tdsAfterCess ?? 0),
              salaryHoldFrom: activeResignation?.salaryHoldFrom ?? null,
              salaryHoldTo: activeResignation?.salaryHoldTo ?? null,
              esi: Math.ceil(ip.esi ?? 0),
              esiEmployer: Math.ceil(ip.esiEmployer ?? 0)
            }
            // console.log('expense 6')
            // Before calling calculation, determine active resignation and LOP

            // const localResults = this.payrollCalcService.calculateGrossMetrics(
            //   context,
            //   ip.employee.joiningDate,
            //   activeResignation?.salaryHoldFrom,
            //   activeResignation?.salaryHoldTo,
            //   activeResignation ? true : false
            // )

            //for off boarding
            const employee = (
              context.currentSalary as EmployeeSalary & {
                employee?: {
                  employeeResignations?: EmployeeResignation[]
                }
              }
            ).employee

            const resignations: EmployeeResignation[] = employee?.employeeResignations ?? []

            const validResignation =
              resignations
                .filter(
                  (resignation: EmployeeResignation) =>
                    resignation.status === 'Approved' && !resignation.isFullNFinal
                )
                .sort((a: EmployeeResignation, b: EmployeeResignation) => {
                  const aDate = a.effectiveLastDay ? new Date(a.effectiveLastDay).getTime() : 0

                  const bDate = b.effectiveLastDay ? new Date(b.effectiveLastDay).getTime() : 0

                  return bDate - aDate
                })[0] ?? null

            const effectiveLastDay = validResignation?.effectiveLastDay
              ? new Date(validResignation.effectiveLastDay)
              : null

            console.log('employee effective last date:', effectiveLastDay)
            const localResults = this.payrollCalcService.calculateGrossMetrics(
              context,
              ip.employee.joiningDate,
              // activeResignation?.effectiveLastDay ?? null,
              effectiveLastDay,
              activeResignation?.salaryHoldFrom ?? null,
              activeResignation?.salaryHoldTo ?? null,
              activeResignation?.isSalaryHold === true
            )
            // console.log('local results 333', localResults)
            const tdsResult = tdsMap![ip.employeeId] // Use non-null assertion as we filtered

            return {
              incomingPayroll: ip,
              previousSalary: prevSalariesMap?.[ip.employeeId] ?? null,
              lopDaysForArrears: arrearLopsMap?.[ip.employeeId] ?? 0,
              initialGross: localResults.grossSalary,
              initialBreakdown: localResults.calculationBreakdown,
              initialTds: tdsResult?.tdsForCurrentMonth ?? 0,
              initialNet: tdsResult?.finalNetSalary ?? localResults.grossSalary,
              initialPayrollCalculationContext: context,
              initialEsi: tdsResult?.esiForCurrentMonth ?? 0,
              initialEsiEmployer: tdsResult?.esiEmployerForCurrentMonth ?? 0
            }
          })
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(payrollsWithContext => {
        this.buildFormControls(payrollsWithContext)
      })
    //  map(([_, prevSalariesMap, arrearLopsMap]) => {
    //    return this.transformDataForForm(prevSalariesMap, arrearLopsMap)
    //  }),
    //  takeUntil(this.destroy$)
    //)
    //.subscribe(payrollsWithContext => {
    //  this.buildFormControls(payrollsWithContext)
    //})
  }

  //Helpers required for subscribeToDataAndBuildForm()
  private handleDataFetchingNeeds(
    runningPayrollBatch: IncomingRunningPayrollBatch,
    prevSalariesMap: Record<string, EmployeeSalary | null> | null,
    arrearLopsMap: Record<string, number> | null
  ): void {
    this.payPeriodStartDate = runningPayrollBatch.payPeriodStart
    this.payPeriodEndDate = runningPayrollBatch.payPeriodEnd

    const filteredIncomingPayrolls = this.filterRelevantPayrolls(
      runningPayrollBatch.payrolls,
      this.payPeriodEndDate
    )
    this.currentIncomingPayrolls = filteredIncomingPayrolls // For template

    if (filteredIncomingPayrolls.length === 0) {
      this.payrollsFormArray.clear()
      return
    }

    //Fetch Previous Salaries and Arrear Lop Days
    const { prevSalaryRequests, arrearLopRequests } = this.prepareDataLoadRequests(
      filteredIncomingPayrolls,
      prevSalariesMap,
      arrearLopsMap,
      this.payPeriodStartDate,
      this.payPeriodEndDate
    )

    if (prevSalaryRequests.length > 0) {
      this.store.dispatch(
        runningPayrollActions.loadPreviousSalaries({ requests: prevSalaryRequests })
      )
    }
    if (arrearLopRequests.length > 0) {
      this.store.dispatch(runningPayrollActions.loadArrearLopDays({ requests: arrearLopRequests }))
    }
  }

  private buildFormControls(
    payrollsWithContext: {
      incomingPayroll: IncomingPayroll
      previousSalary: EmployeeSalary | null
      lopDaysForArrears: number
      initialGross: number
      initialBreakdown: PayrollCalculationBreakdown
      initialTds: number
      initialNet: number
      initialPayrollCalculationContext: PayrollCalculationContext
      initialEsi: number
      initialEsiEmployer: number
    }[]
  ): void {
    if (!payrollsWithContext) return
    this.payrollsFormArray.clear({ emitEvent: false })

    const payrollControls = payrollsWithContext.map(pwc => this.createPayrollFG(pwc))
    payrollControls.forEach(control => this.payrollsFormArray.push(control, { emitEvent: false }))

    if (payrollControls.length > 0) {
      this.payrollsFormArray.updateValueAndValidity() // Trigger calculations
      this.payrollBatchForm.markAsPristine() // Start with a clean form
    }
  }

  /** Creates a single FormGroup for an employee's payroll and sets up its recalculation logic. */
  private createPayrollFG(payrollContext: {
    incomingPayroll: IncomingPayroll
    previousSalary: EmployeeSalary | null
    lopDaysForArrears: number
    initialGross: number
    initialBreakdown: PayrollCalculationBreakdown
    initialTds: number
    initialNet: number
    initialPayrollCalculationContext: PayrollCalculationContext
    initialEsi: number
    initialEsiEmployer: number
  }): FormGroup {
    const {
      incomingPayroll,
      previousSalary,
      lopDaysForArrears,
      initialGross,
      initialTds,
      initialNet,
      initialBreakdown,
      initialEsi,
      initialEsiEmployer
    } = payrollContext
    const daysInPayPeriod = getDaysInPeriod(
      new Date(this.payPeriodStartDate),
      new Date(this.payPeriodEndDate)
    )
    const toggleEsiControls = (gross: number) => {
      const esiControl = fg.get('esi')
      const esiEmployerControl = fg.get('esiEmployer')

      if (gross > 21000) {
        esiControl?.disable({ emitEvent: false })
        esiEmployerControl?.disable({ emitEvent: false })
      } else {
        esiControl?.enable({ emitEvent: false })
        esiEmployerControl?.enable({ emitEvent: false })
      }
    }
    const fg = this.fb.group({
      id: [incomingPayroll.id],
      empId: [incomingPayroll.employee.empId],
      employeeId: [incomingPayroll.employeeId, Validators.required],
      belongsTo: [incomingPayroll.belongsTo, Validators.required],
      salaryId: [incomingPayroll.salaryId, Validators.required],
      lopDays: [incomingPayroll.lopDays ?? 0, [Validators.min(0), Validators.max(daysInPayPeriod)]],
      bonus: [incomingPayroll.bonus ?? 0, Validators.min(0)],
      expenses: [incomingPayroll.expenses ?? 0, Validators.min(0)],
      adjustments: [incomingPayroll.adjustments ?? 0],
      rounding: [incomingPayroll.rounding ?? 0],
      status: [incomingPayroll.status, Validators.required],
      // Disabled fields are calculated
      grossSalary: [initialGross ?? 0],
      netSalary: [initialNet ?? 0],
      tdsAfterCess: [
        incomingPayroll.tdsAfterCess ? incomingPayroll.tdsAfterCess : (initialTds ?? 0)
      ],
      calculationBreakdown: [initialBreakdown],
      esi: [incomingPayroll.esi ? incomingPayroll.esi : (initialEsi ?? 0)],
      // esiEmployer: [
      //   incomingPayroll.esiEmployer ? incomingPayroll.esiEmployer : (initialEsiEmployer ?? 0)
      // ]
      esiEmployer: [
        { value: incomingPayroll.esiEmployer ?? initialEsiEmployer ?? 0, disabled: true }
      ]
      // initialGross <= 21000 ? (initialGross * 0.75) / 100 : 0]
    })
    toggleEsiControls(initialGross)
    // Reactive recalculation stream for this specific employee's form group
    const recalculate$ = combineLatest([
      fg.get('lopDays')!.valueChanges.pipe(startWith(fg.get('lopDays')!.value)),
      fg.get('bonus')!.valueChanges.pipe(startWith(fg.get('bonus')!.value)),
      fg.get('expenses')!.valueChanges.pipe(startWith(fg.get('expenses')!.value)),
      fg.get('adjustments')!.valueChanges.pipe(startWith(fg.get('adjustments')!.value)),
      fg.get('rounding')!.valueChanges.pipe(startWith(fg.get('rounding')!.value)),
      fg.get('tdsAfterCess')!.valueChanges.pipe(startWith(fg.get('tdsAfterCess')!.value)),
      fg.get('esi')!.valueChanges.pipe(startWith(fg.get('esi')!.value)),
      fg.get('esiEmployer')!.valueChanges.pipe(startWith(fg.get('esiEmployer')!.value))
    ]).pipe(
      skip(1), // 👈 This line ignores the initial emission
      debounceTime(500),

      // Map to local gross calculation
      map(([lop, bonus, expenses, adjustments, rounding, tdsAfterCess, esi, esiEmployer]) => {
        // console.log('expense 12')
        const context: PayrollCalculationContext = {
          currentSalary: incomingPayroll.salary,
          previousSalary: previousSalary,
          payPeriodStart: this.payPeriodStartDate,
          payPeriodEnd: this.payPeriodEndDate,
          lopDaysForArrearsPeriod: lopDaysForArrears,
          lopDays: lop ?? 0,
          bonus: bonus ?? 0,
          expenses: expenses ?? 0,
          adjustments: adjustments ?? 0,
          rounding: rounding ?? 0,
          tdsAfterCess: Math.ceil(tdsAfterCess ?? 0),
          netSalary: initialNet ?? incomingPayroll.salary,
          salaryHoldFrom: payrollContext.initialPayrollCalculationContext
            ? payrollContext.initialPayrollCalculationContext.salaryHoldFrom
            : '',
          salaryHoldTo: payrollContext.initialPayrollCalculationContext
            ? payrollContext.initialPayrollCalculationContext.salaryHoldTo
            : '',
          esi: Math.ceil(esi ?? 0) ?? 0,
          esiEmployer: Math.ceil(esiEmployer ?? 0) ?? 0
        }
        console.log('expense 14', context)
        // Before calling calculation, determine active resignation and LOP
        // console.log('context 14', context.currentSalary)
        // const localData = this.payrollCalcService.calculateGrossMetrics(context)
        // const localData = this.payrollCalcService.calculateGrossMetrics(
        //   context,
        //   context.currentSalary.effectiveFrom,
        //   context.payPeriodStart,
        //   context.payPeriodEnd,
        //   payrollContext.initialPayrollCalculationContext.salaryHoldFrom ? true : false
        // )
        //for off boarding
        const employee = (
          context.currentSalary as EmployeeSalary & {
            employee?: {
              employeeResignations?: EmployeeResignation[]
            }
          }
        ).employee

        const resignations: EmployeeResignation[] = employee?.employeeResignations ?? []

        const validResignation =
          resignations
            .filter(
              (resignation: EmployeeResignation) =>
                resignation.status === 'Approved' && !resignation.isFullNFinal
            )
            .sort((a: EmployeeResignation, b: EmployeeResignation) => {
              const aDate = a.effectiveLastDay ? new Date(a.effectiveLastDay).getTime() : 0

              const bDate = b.effectiveLastDay ? new Date(b.effectiveLastDay).getTime() : 0

              return bDate - aDate
            })[0] ?? null

        const effectiveLastDay = validResignation?.effectiveLastDay
          ? new Date(validResignation.effectiveLastDay)
          : null

        const localData = this.payrollCalcService.calculateGrossMetrics(
          context,
          incomingPayroll.employee.joiningDate,
          effectiveLastDay,
          context.payPeriodStart,
          context.payPeriodEnd,
          payrollContext.initialPayrollCalculationContext.salaryHoldFrom ? true : false
        )

        console.log('local data 536', localData)
        // console.log('current salary', context.currentSalary)
        return {
          localData,
          lop,
          bonus,
          expenses,
          adjustments,
          rounding,
          tdsAfterCess,
          esi,
          esiEmployer
        }
      }),
      distinctUntilChanged(
        (prev, curr) =>
          prev.localData.grossSalary === curr.localData.grossSalary &&
          prev.localData.netSalary === curr.localData.netSalary
      ),
      takeUntil(this.destroy$)
    )

    recalculate$.subscribe(localResults => {
      fg.patchValue(
        {
          grossSalary: Math.round(localResults.localData.grossSalary),
          calculationBreakdown: localResults.localData.calculationBreakdown
        },
        { emitEvent: false }
      )
      fg.get('netSalary')?.disable({ emitEvent: false })
      const gross = localResults.localData.grossSalary

      const esiControl = fg.get('esi')
      const esiEmployerControl = fg.get('esiEmployer')

      if (gross > 21000) {
        esiControl?.disable({ emitEvent: false })
        esiEmployerControl?.disable({ emitEvent: false })
      } else {
        esiControl?.enable({ emitEvent: false })
        esiEmployerControl?.enable({ emitEvent: false })
      }
      const payload: TdsCalculationInput = {
        employeeId: incomingPayroll.employeeId,
        payPeriodEnd: this.payPeriodEndDate,
        currentMonthGross: localResults.localData.grossSalary,
        lopDays: localResults.lop ?? 0,
        bonus: localResults.bonus ?? 0,
        expenses: localResults.expenses ?? 0,
        adjustments: localResults.adjustments ?? 0,
        rounding: localResults.rounding ?? 0,
        tdsAfterCess: Math.ceil(localResults.tdsAfterCess ?? 0),
        esi: Math.ceil(localResults.esi ?? 0),
        esiEmployer: Math.ceil(localResults.esiEmployer ?? 0)
      }

      // console.log('expense 17')

      // Dispatch the action
      this.store.dispatch(tdsCalculationActions.loadTdsPreview({ payload }))
    })

    return fg
  }

  private handleSingleRowUpdates(): void {
    this.store
      .select(selectTdsCalculation)
      .pipe(
        filter(state => state !== null),
        takeUntil(this.destroy$)
      )
      .subscribe(state => {
        // Find the correct FormGroup in the FormArray
        const control = this.payrollsFormArray.controls.find(
          c => c.get('employeeId')?.value === state.employeeId
        ) as FormGroup

        if (control) {
          control.patchValue(
            {
              tdsAfterCess: Math.ceil(state!.result.tdsForCurrentMonth),
              netSalary: state!.result.finalNetSalary,
              esi: Math.ceil(state!.result.esiForCurrentMonth ?? 0),
              esiEmployer: Math.ceil(state!.result.esiEmployerForCurrentMonth ?? 0)
            },
            { emitEvent: false }
          )
          control.get('netSalary')?.enable({ emitEvent: false })
        }
      })
  }

  syncLopDays(): void {
    this.runningPayroll$
      .pipe(
        filter(
          (rp): rp is IncomingRunningPayrollBatch =>
            !!rp && !!rp.belongsTo && !!rp.payPeriodStart && !!rp.payPeriodEnd
        ),
        take(1),
        switchMap(batch => {
          return this.ClockInClockOutService.getLopDaysForEmployees(
            batch.payPeriodStart,
            batch.payPeriodEnd,
            batch.belongsTo
          )
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: lopDataFromServer => {
          let updatedCount = 0
          this.payrollsFormArray.controls.forEach(control => {
            const employeeId = control.get('employeeId')?.value
            const lopEntry = lopDataFromServer.find(lop => lop.employeeId === employeeId)
            if (lopEntry && control.get('lopDays')?.value !== lopEntry.lopDays) {
              control.get('lopDays')?.patchValue(lopEntry.lopDays)
              updatedCount++
            }
          })
          if (updatedCount > 0) this.payrollBatchForm.markAsDirty()
        },
        error: err => this.toastr.error('Error syncing LOP days.', err.message)
      })
  }

  onSubmit(goToNextScreen: boolean): void {
    if (this.payrollBatchForm.invalid) {
      this.payrollBatchForm.markAllAsTouched()
      return
    }

    this.runningPayroll$.pipe(take(1)).subscribe(currentBatch => {
      if (!currentBatch) return

      // Separate empIds from the the payrolls and update their status.
      const rawPayrolls: (Payroll & { empId: string })[] =
        this.payrollBatchForm.getRawValue().payrolls
      const payrolls = rawPayrolls.map(p => {
        const { empId, ...payroll } = p
        // Clone calculationBreakdown safely
        const breakdown = payroll.calculationBreakdown

        if (!breakdown) {
          return {
            ...payroll,
            status: goToNextScreen ? PayrollStatus.IN_PROCESS : PayrollStatus.DRAFT
          }
        }

        // const deductions = breakdown.deductions.map(d =>
        //   d.name === 'ESI' ? { ...d, amount: payroll.esi ?? 0 } : d
        // )

        // // 👉 Check if ESI already exists
        // const hasEsi = deductions.some(d => d.name === 'ESI')

        // // 👉 If not exists, add it
        // if (!hasEsi && payroll.esi) {
        //   deductions.push({
        //     name: 'ESI',
        //     amount: payroll.esi
        //   })
        // }

        // Deep copy deductions
        let deductions = structuredClone(breakdown.deductions || [])

        // Employer contribution array
        const employerContribution = structuredClone(
          breakdown.deductions?.filter((d: any) => d.name.includes('ESI (Employer')) ?? []
        )

        // Current ESI amount
        const esiAmount = payroll.esi ?? 0
        const employerEsiAmount = payroll.esiEmployer ?? 0
        // Find all possible ESI entries
        const idxPlain = deductions.findIndex((d: any) => d.name === 'ESI')
        const idxEmployee = deductions.findIndex((d: any) => d.name.includes('ESI (Employee'))
        const idxEmployer = employerContribution.findIndex((d: any) =>
          d.name.includes('ESI (Employer')
        )
        if (idxEmployer !== -1) {
          employerContribution[idxEmployer] = {
            ...employerContribution[idxEmployer],
            amount: employerEsiAmount
          }
        } else if (employerEsiAmount > 0) {
          employerContribution.push({
            name: 'ESI (Employer Contribution)',
            amount: employerEsiAmount
          })
        }
        /**
         * LOGIC:
         * 1. Update Employee Contribution if exists
         * 2. Else update plain ESI
         * 3. Else add Employee Contribution
         */
        if (idxEmployee !== -1) {
          deductions[idxEmployee] = {
            ...deductions[idxEmployee],
            amount: esiAmount
          }
        } else if (idxPlain !== -1) {
          deductions[idxPlain] = {
            ...deductions[idxPlain],
            amount: esiAmount
          }
        } else if (esiAmount > 0) {
          deductions.push({
            name: 'ESI (Employee Contribution)',
            amount: esiAmount
          })
        }

        // Cleanup duplicate plain ESI
        if (idxEmployee !== -1 && idxPlain !== -1) {
          deductions = deductions.filter((_: any, i: number) => i !== idxPlain)
        }

        const payrollWithStatus = {
          ...payroll,
          calculationBreakdown: {
            earnings: breakdown.earnings,
            deductions,
            employerContribution
          },
          status: goToNextScreen ? PayrollStatus.IN_PROCESS : PayrollStatus.DRAFT
        }
        return payrollWithStatus
      })
      const totalAmount = payrolls.reduce((sum, p) => sum + (p.grossSalary ?? 0), 0)
      const totalTdsAfterCess = payrolls.reduce((sum, p) => sum + Math.ceil(p.tdsAfterCess ?? 0), 0)
      const totalEsiEmployee = payrolls.reduce((sum, p) => sum + Math.ceil(p.esi ?? 0), 0)
      const totalEsiEmployer = payrolls.reduce((sum, p) => sum + Math.ceil(p.esiEmployer ?? 0), 0)

      const totalNetAmount = payrolls.reduce((sum, p) => sum + (p.netSalary ?? 0), 0)
      // Get the user id of the current logged-in user to log who ran the current payroll
      let userId = ''
      this.store
        .select(selectUser)
        .pipe(take(1))
        .subscribe(user => {
          if (!user) return
          userId = user.id
        })

      const batchData: RunningPayrollBatch = {
        id: currentBatch.id,
        belongsTo: currentBatch.belongsTo,
        payPeriodStart: currentBatch.payPeriodStart,
        payPeriodEnd: currentBatch.payPeriodEnd,
        investmentsLockedAt: null, // Or set as needed
        payrolls: payrolls,
        totalAmount: Number(totalAmount.toFixed(2)),
        totalTdsAfterCess: Math.ceil(Number(totalTdsAfterCess.toFixed(2))),
        netAmount: Number(totalNetAmount.toFixed(2)),
        employeeCount: payrolls.length,
        status: goToNextScreen ? PayrollStatus.IN_PROCESS : PayrollStatus.DRAFT,
        processedBy: goToNextScreen ? userId : null,
        paidBy: null,
        totalEsiEmployee: Number(totalEsiEmployee),
        totalEsiEmployer: Number(totalEsiEmployer)
      }
      console.log(payrolls)
      console.log(batchData)
      console.log(batchData.payrolls ? batchData.payrolls[0].calculationBreakdown : '')
      this.store.dispatch(runningPayrollActions.saveRunningPayrollDraft({ batchData }))
      if (goToNextScreen) {
        this.nextScreen.emit(true)
      } else {
        this.router.navigate([eps.hrm.managePayrollPath])
      }
    })
  }

  // openModal(modal: unknown, index: number) {
  //   const fg = this.payrollsFormArray.at(index) as FormGroup
  //   const originalPayroll = this.currentIncomingPayrolls[index]
  //   // console.log('originalpayroll', originalPayroll)
  //   // console.log('formvalues', fg.getRawValue())
  //   if (!fg || !originalPayroll) {
  //     this.toastr.error('Could not find payroll data for modal.')
  //     return
  //   }

  //   const formValues = fg.getRawValue()

  //   // Safely get breakdown
  //   const breakdown = originalPayroll.calculationBreakdown ||
  //     formValues.calculationBreakdown || {
  //       earnings: [],
  //       deductions: []
  //     }

  //   // Update deductions
  //   // const updatedDeductions = (breakdown.deductions || []).map((d: any) =>
  //   //   d.name === 'ESI' ? { ...d, amount: formValues.esi ?? originalPayroll.esi ?? 0 } : d
  //   // )
  //   const updatedDeductions = [...(breakdown.deductions || [])]

  //   const esiAmount = formValues.esi ?? originalPayroll.esi ?? 0

  //   const esiIndex = updatedDeductions.findIndex((d: any) => d.name === 'ESI')

  //   // ✅ If exists → update
  //   if (esiIndex !== -1) {
  //     updatedDeductions[esiIndex] = {
  //       ...updatedDeductions[esiIndex],
  //       amount: esiAmount
  //     }
  //   }
  //   // ✅ If not exists → add
  //   else if (esiAmount) {
  //     updatedDeductions.push({
  //       name: 'ESI',
  //       amount: esiAmount
  //     })
  //   }

  //   // console.log('esi', updatedDeductions)
  //   const hasPlainESI = updatedDeductions.some((d: any) => d.name?.trim() === 'ESI')

  //   const hasEmployeeESI = updatedDeductions.some((d: any) => d.name?.includes('ESI (Employee'))

  //   const hasEmployerESI = updatedDeductions.some((d: any) => d.name?.includes('ESI (Employer'))

  //   const cleanedDeductions =
  //     hasPlainESI && hasEmployeeESI && hasEmployerESI
  //       ? updatedDeductions.filter((d: any) => d.name?.trim() !== 'ESI')
  //       : updatedDeductions
  //   const payrollData = {
  //     ...originalPayroll,
  //     ...formValues,
  //     calculationBreakdown: {
  //       earnings: breakdown.earnings || [],
  //       deductions: cleanedDeductions
  //     }
  //   }
  //   const modalRef = this.modalService.open(modal, {
  //     size: 'lg',
  //     centered: true,
  //     scrollable: false
  //   })
  //   // const formValues = fg.getRawValue()
  //   // modalRef.componentInstance.payrollData = { ...originalPayroll, ...formValues }
  //   modalRef.componentInstance.payrollData = payrollData
  //   modalRef.componentInstance.payPeriodStartDate = this.payPeriodStartDate
  //   modalRef.componentInstance.payPeriodEndDate = this.payPeriodEndDate
  // }

  openModal(modal: unknown, index: number) {
    const fg = this.payrollsFormArray.at(index) as FormGroup
    const originalPayroll = this.currentIncomingPayrolls[index]

    if (!fg || !originalPayroll) {
      this.toastr.error('Could not find payroll data for modal.')
      return
    }

    const formValues = fg.getRawValue()
    const breakdown = structuredClone(formValues.calculationBreakdown) ||
      structuredClone(originalPayroll.calculationBreakdown) || {
        earnings: [],
        deductions: []
      }

    // 1. Naya ESI amount (Jo UI se aa raha hai)
    const esiAmount = formValues.esi ?? originalPayroll.esi ?? 0

    // 2. Deductions ki deep copy lein
    let deductions = structuredClone(breakdown.deductions || [])

    // 3. Sabhi ESI variations ke indices dhunde
    const idxPlain = deductions.findIndex((d: any) => d.name === 'ESI')
    const idxEmployee = deductions.findIndex((d: any) => d.name.includes('ESI (Employee'))
    const idxEmployer = deductions.findIndex((d: any) => d.name.includes('ESI (Employer'))

    /**
     * LOGIC:
     * Agar 'ESI (Employee' milta hai, toh usme amount update karo.
     * Agar nahi milta, toh check karo ki kya plain 'ESI' hai? Usme update karo.
     * Agar dono nahi hain, toh naya add karo.
     */
    if (idxEmployee !== -1) {
      deductions[idxEmployee] = structuredClone({
        ...deductions[idxEmployee],
        amount: esiAmount
      })
    } else if (idxPlain !== -1) {
      deductions[idxPlain] = { ...deductions[idxPlain], amount: esiAmount }
    } else if (esiAmount > 0) {
      deductions.push({ name: 'ESI (Employee Contribution)', amount: esiAmount })
    }

    // 4. CLEANUP: Agar 'Employee' aur 'Plain ESI' dono maujood hain,
    // toh plain waale ko hata dein taaki double deduction na dikhe.
    if (idxEmployee !== -1 && idxPlain !== -1) {
      deductions = deductions.filter((d: any, i: number) => i !== idxPlain)
    }

    const payrollData = {
      ...originalPayroll,
      ...formValues,
      calculationBreakdown: {
        earnings: structuredClone(breakdown.earnings || []),
        deductions: deductions
      }
    }

    const modalRef = this.modalService.open(modal, {
      size: 'lg',
      centered: true,
      scrollable: false
    })

    modalRef.componentInstance.payrollData = payrollData
    modalRef.componentInstance.payPeriodStartDate = this.payPeriodStartDate
    modalRef.componentInstance.payPeriodEndDate = this.payPeriodEndDate
  }

  onLopInput(payrollCtrl: AbstractControl) {
    payrollCtrl.get('tdsAfterCess')?.setValue(0)
    payrollCtrl.get('esi')?.setValue(0)
    // payrollCtrl.get('esiEmployer')?.setValue(0)
    const value = payrollCtrl.get('lopDays')?.value
    const daysInPayPeriod = getDaysInPeriod(
      new Date(this.payPeriodStartDate),
      new Date(this.payPeriodEndDate)
    )

    if (value > 31) {
      payrollCtrl.get('lopDays')?.setValue(daysInPayPeriod)
    } else if (value < 0) {
      payrollCtrl.get('lopDays')?.setValue(0)
    }
  }

  private setFinancialYear() {
    this.runningPayroll$
      .pipe(
        filter(runningPayroll => !!runningPayroll),
        take(1)
      )
      .subscribe(runningPayroll => {
        const startDate = new Date(runningPayroll.payPeriodStart)
        const startYear = startDate.getFullYear()
        const startMonth = startDate.getMonth() // 0 = Jan, 3 = Apr

        const financialYearStart = startMonth >= 3 ? startYear : startYear - 1
        const financialYearEnd = financialYearStart + 1

        this.financialYear = `${financialYearStart}-${financialYearEnd}`
      })
  }

  private initializeBusinessEntityObservable(): void {
    this.businessEntity$ = combineLatest([
      this.runningPayroll$.pipe(filter((rp): rp is IncomingRunningPayrollBatch => !!rp)),
      this.store
        .select(selectBusinesses)
        .pipe(filter(businesses => !!businesses && businesses.length > 0))
    ]).pipe(
      map(([runningPayroll, businesses]) => {
        return businesses.find(b => b.id === runningPayroll.belongsTo)?.businessName
      }),
      takeUntil(this.destroy$)
    )
  }

  onFileSelect(ev: Event): void {
    const e = ev.currentTarget as HTMLInputElement
    const fileList: FileList | null = e.files

    if (fileList && fileList.length > 0) {
      const file = fileList[0]
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        this.toastr.error('Invalid file type. Please upload a CSV file.')
        return
      }

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: result => {
          if (!result.meta.fields?.includes('empId')) {
            this.toastr.error(
              'CSV file is missing the required "empId" header.',
              'Invalid CSV Header'
            )
            return
          }
          this.processOverrideUpload(result.data as PayrollOverrideRow[])
        },
        error: error => this.toastr.error('Error parsing CSV file.', error.message)
      })

      e.value = ''
    }
  }

  private processOverrideUpload(data: PayrollOverrideRow[]): void {
    let updatedCount = 0
    let notFoundCount = 0
    const notFoundIds: string[] = []

    data.forEach(row => {
      const empIdFromCsv = row.empId?.trim()
      if (!empIdFromCsv) return

      const c = this.payrollsFormArray.controls.find(c => c.get('empId')?.value === empIdFromCsv)

      if (c) {
        const parse = (val: string | undefined) => (val?.trim() ? parseFloat(val) : NaN)

        const f = {
          lopDays: parse(row.lopDays),
          bonus: parse(row.bonus),
          expenses: parse(row.expenses),
          adjustments: parse(row.adjustments),
          rounding: parse(row.rounding),
          grossSalary: parse(row.grossSalary),
          netSalary: parse(row.netSalary),
          tdsAfterCess: parse(row.tdsAfterCess),
          esi: parse(row.esi),
          esiEmployer: parse(row.esiEmployer)
        }

        const cp: Record<string, number | null> = Object.entries(f)
          // eslint-disable-next-line @typescript-eslint/naming-convention
          .filter(([_, value]) => !isNaN(value))
          .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {})

        if (Object.prototype.hasOwnProperty.call(cp, 'grossSalary')) {
          cp['calculationBreakdown'] = null
        }

        if (Object.keys(cp).length > 0) {
          c.patchValue(cp, { emitEvent: false })
          updatedCount++
        }
      } else {
        notFoundCount++
        notFoundIds.push(empIdFromCsv)
      }
    })

    // Provide feedback
    if (updatedCount > 0) {
      this.payrollBatchForm.markAsDirty()
      this.toastr.success(
        `Successfully overrode data for ${updatedCount} employee(s). Their values are now locked until manually edited.`
      )
    }
    if (notFoundCount > 0) {
      this.toastr.warning(
        `${notFoundCount} Employee ID(s) from the CSV were not found in this payroll.`
      )
    }
  }

  onBonusInput(payrollCtrl: AbstractControl) {
    payrollCtrl.get('tdsAfterCess')?.setValue(0)
    payrollCtrl.get('esi')?.setValue(0)
    // payrollCtrl.get('esiEmployer')?.setValue(0)
  }

  get showEsiColumn(): boolean {
    if (!this.payPeriodStartDate) return false

    const payStart = new Date(this.payPeriodStartDate)
    const esiStartDate = new Date('2025-11-01')

    return payStart >= esiStartDate
  }

  // onEsiInput(payrollCtrl: AbstractControl) {
  //   console.log('esi payrollctrl', payrollCtrl.get('esi')?.value)
  //   payrollCtrl.get('esi')?.setValue(0)
  // }
}
